import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { IPostResponse } from '@app/core/interfaces/IPostResponse';
import { environment } from '@env/environment';
import { BehaviorSubject, firstValueFrom, from, Observable, of, ReplaySubject, Subscription, throwError, timer } from 'rxjs';
import { catchError, map, mergeMap, finalize } from 'rxjs/operators';
import { BrowserStorageService } from '../storage/browser-storage.service';
import { JwtHelperService } from './jwt-helper.service';
import { UserAccessToken } from './model/auth-user.interface';
import { GlobalVariablesService } from '../global-variables.service';
import { Guid } from '@app/core/constants/constants';
import { getPublicEntryPath, navigateToPublicEntry } from '../public-entry.util';
import { AppPermissionService } from '../app-permission.service';

@Injectable({ providedIn: 'root' })
export class BadisOauthLoginService {
    private _userAccessToken: UserAccessToken;
    private tokenLoaded: boolean = false;
    private sessionExpirySub: Subscription;
    private sessionRemainingTickerSub: Subscription;
    private sessionRemainingMsSubject = new BehaviorSubject<number>(0);
    private publicAuthConfigPromise?: Promise<PublicAuthConfig>;
    public readonly sessionRemainingMs$ = this.sessionRemainingMsSubject.asObservable();

    public get userAccessToken(): UserAccessToken {
        if (!this.tokenLoaded) {
            const token = this.loadLoginToken();
            if (token && !this.jwtHelperService.isTokenExpired(token.accessToken)) {
                this._userAccessToken = token;
            }
            this.tokenLoaded = true;
        }
        return this._userAccessToken;
    }

    public set userAccessToken(value: UserAccessToken) {
        this._userAccessToken = Object.freeze(value);
        this.storeLoginToken(value);
        this.tokenLoaded = true;
    }

    public authStatusSubject: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    public onAuthStatusChanged = this.authStatusSubject.asObservable();

    private refreshSub: Subscription;

    constructor(
        private http: HttpClient,
        private jwtHelperService: JwtHelperService,
        private browserStorageService: BrowserStorageService,
        private globals: GlobalVariablesService,
        private injector: Injector
    ) {
        this.updateStatusOnPageRefresh();
    }

    public invokeAuthStatusChanged(isAuthenticated: boolean): void {
        this.authStatusSubject.next(isAuthenticated);
    }

    private headerSetter(): HttpHeaders {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Methods': 'GET, POST, PATCH, PUT, DELETE, OPTIONS',
            'Access-Control-Allow-Headers': 'Origin, Content-Type, X-Auth-Token'
        });
    }

    public loginUser(loginModel: any): any {
        return from(this.fetchPublicAuthConfig()).pipe(
            mergeMap((authConfig) => {
                if (authConfig.provider === 'Supabase' && authConfig.authBaseUrl && authConfig.anonKey) {
                    return this.loginWithSupabase(loginModel, authConfig);
                }

                return this.loginWithLocalCredentials(loginModel);
            }),
            catchError((error: HttpErrorResponse | Error) => {
                this.invokeAuthStatusChanged(false);
                return throwError(() => new Error(error.message));
            })
        );
    }

    private loginWithLocalCredentials(loginModel: any): any {
        return this.http
            .post(`${this.getBaseUrl()}/account/login`, JSON.stringify(loginModel), { headers: this.headerSetter() })
            .pipe(
                mergeMap((response: IPostResponse) => {
                    if (!response.success) {
                        return of(response);
                    }

                    return this.completeUserLoginToken(response.token).pipe(
                        map(() => response)
                    );
                }),
            );
    }

    private loginWithSupabase(loginModel: any, authConfig: PublicAuthConfig): any {
        const email = loginModel?.userName ?? loginModel?.UserName ?? loginModel?.email ?? loginModel?.Email ?? '';
        const password = loginModel?.password ?? loginModel?.Password ?? '';
        const supabaseHeaders = new HttpHeaders({
            'Content-Type': 'application/json',
            apikey: authConfig.anonKey
        });

        return this.http
            .post<any>(
                `${authConfig.authBaseUrl}/token?grant_type=password`,
                { email, password },
                { headers: supabaseHeaders }
            )
            .pipe(
                mergeMap((supabaseResponse) => {
                    const accessToken = supabaseResponse?.access_token;
                    if (!accessToken) {
                        throw new Error('Supabase login did not return an access token.');
                    }

                    return this.http.post<IPostResponse>(
                        `${this.getBaseUrl()}/account/supabase-login`,
                        { accessToken },
                        { headers: this.headerSetter() }
                    );
                }),
                mergeMap((response: IPostResponse) => {
                    if (!response.success) {
                        return of(response);
                    }

                    return this.completeUserLoginToken(response.token).pipe(
                        map(() => response)
                    );
                })
            );
    }

    public isUserLoggedIn(): boolean {
        const token = this.userAccessToken;
        return !!token && !this.jwtHelperService.isTokenExpired(token.accessToken);
    }

    public logout(): void {
        const token: UserAccessToken = this.userAccessToken;
        if (token) {
            this.http
                .post<IPostResponse>(`${this.getBaseUrl()}/account/logout`, { refreshToken: token.refreshToken }, { headers: this.headerSetter() })
                .pipe(finalize(() => this.clearSession()))
                .subscribe();
        } else {
            this.clearSession();
        }
    }

    private clearSession() {
        this.browserStorageService.removeLocal('badis_oauth_token');
        this.resolvePermissionService()?.clear();
        this.unscheduleRefreshToken();
        this.unscheduleSessionExpiry();
        this.unscheduleSessionRemainingTicker();
        this.sessionRemainingMsSubject.next(0);
        this._userAccessToken = null;
        this.tokenLoaded = false;
        this.invokeAuthStatusChanged(false);

        const router = this.resolveRouter();
        if (router) {
            void navigateToPublicEntry(router, { replaceUrl: true });
            return;
        }

        if (typeof window !== 'undefined' && window.location) {
            window.location.replace(getPublicEntryPath());
        }
    }

    private resolveRouter(): Router | null {
        try {
            return this.injector.get(Router, null);
        } catch {
            return null;
        }
    }

    private getBaseUrl(): string {
        return environment.api;
    }

    private fetchPublicAuthConfig(): Promise<PublicAuthConfig> {
        if (!this.publicAuthConfigPromise) {
            this.publicAuthConfigPromise = firstValueFrom(this.http
                .get<PublicAuthConfig>(`${environment.apiv2}/bootstrap-setup/public-auth`))
                .then((config) => ({
                    provider: config?.provider ?? 'Local',
                    projectUrl: config?.projectUrl ?? '',
                    anonKey: config?.anonKey ?? '',
                    authBaseUrl: config?.authBaseUrl ?? ''
                }))
                .catch(() => ({
                    provider: 'Local',
                    projectUrl: '',
                    anonKey: '',
                    authBaseUrl: ''
                }));
        }

        return this.publicAuthConfigPromise;
    }

    public processUserLoginToken(token: string): void {
        const jwtData: any = this.jwtHelperService.decodeToken(token);
        this.userAccessToken = {
            accessToken: token,
            refreshToken: jwtData.refreshToken,
            expires: jwtData.exp,
            avatar: jwtData.avatar,
            userName: jwtData.userName,
            userId: jwtData.userId,
            roles: this.normalizeRoles(jwtData.roles),
            displayName: jwtData.displayName,
            sessionName: jwtData.sessionName,
        };
        this.storeLoginToken(this.userAccessToken);
    }

    public completeUserLoginToken(token: string, emitAuthStatus = true): Observable<void> {
        this.processUserLoginToken(token);
        this.scheduleTokenRefresh();

        const permissionService = this.resolvePermissionService();
        if (!permissionService) {
            if (emitAuthStatus) {
                this.invokeAuthStatusChanged(true);
            }
            return of(void 0);
        }

        permissionService.clear();
        return permissionService.loadCurrent(true).pipe(
            map(() => {
                if (emitAuthStatus) {
                    this.invokeAuthStatusChanged(true);
                }
                return void 0;
            })
        );
    }

    private loadLoginToken(): UserAccessToken {
        return this.browserStorageService.getLocal<UserAccessToken>('badis_oauth_token');
    }

    private storeLoginToken(token: UserAccessToken): void {
        this.browserStorageService.setLocal('badis_oauth_token', token);
    }

    private unscheduleRefreshToken(): void {
        if (this.refreshSub) {
            this.refreshSub.unsubscribe();
        }
    }

    private unscheduleSessionExpiry(): void {
        if (this.sessionExpirySub) {
            this.sessionExpirySub.unsubscribe();
        }
    }

    private unscheduleSessionRemainingTicker(): void {
        if (this.sessionRemainingTickerSub) {
            this.sessionRemainingTickerSub.unsubscribe();
        }
    }

    private scheduleSessionRemainingTicker(expiresAtMs: number): void {
        this.unscheduleSessionRemainingTicker();
        this.sessionRemainingMsSubject.next(Math.max(0, expiresAtMs - Date.now()));
        this.sessionRemainingTickerSub = timer(0, 1000).subscribe(() => {
            const remainingMs = Math.max(0, expiresAtMs - Date.now());
            this.sessionRemainingMsSubject.next(remainingMs);
            if (remainingMs <= 0) {
                this.unscheduleSessionRemainingTicker();
            }
        });
    }

    private updateStatusOnPageRefresh(): void {
        const isAuthenticated = this.isUserLoggedIn();
        if (isAuthenticated) {
            this.scheduleTokenRefresh();
        }
        this.invokeAuthStatusChanged(isAuthenticated);
    }

    public scheduleTokenRefresh(): void {
        const token = this.userAccessToken;
        if (token) {
            this.unscheduleRefreshToken();
            this.unscheduleSessionExpiry();

            const expiresAt = new Date(0);
            expiresAt.setUTCSeconds(token.expires);
            const now = new Date();
            const delay = expiresAt.getTime() - now.getTime() - (120 * 1000); // Refresh 2 minutes before expiry

            this.refreshSub = timer(delay).subscribe(() => this.refreshToken());
            const sessionExpiryDelay = Math.max(0, expiresAt.getTime() - now.getTime());
            this.sessionExpirySub = timer(sessionExpiryDelay).subscribe(() => this.clearSession());
            this.scheduleSessionRemainingTicker(expiresAt.getTime());
        } else {
            this.unscheduleSessionRemainingTicker();
            this.sessionRemainingMsSubject.next(0);
        }
    }

    private refreshToken(): void {
        const token = this.userAccessToken;
        if (token) {
            this.http
                .post(`${this.getBaseUrl()}/account/refresh-token`, { refreshToken: token.refreshToken }, { headers: this.headerSetter() })
                .pipe(
                    mergeMap((response: any) => this.completeUserLoginToken(response.token, false)),
                    catchError((error: HttpErrorResponse) => {
                        this.clearSession();
                        return throwError(() => new Error(error.message));
                    })
                )
                .subscribe();
        }
    }

    public resetPassword(userModel: any): any {
        const url: string = `${this.getBaseUrl()}/users/change-password`;
        return this.http
            .put(url, userModel, { headers: this.headerSetter() })
            .pipe(
                map((response: any) => response),
                catchError((error: HttpErrorResponse) => throwError(error))
            );
    }

    public forgotPassword(email: string): any {
        const language = localStorage.getItem('language') ?? 'tr';
        return this.http
            .post(`${this.getBaseUrl()}/users/forgot-password`, { email: email, language }, { headers: this.headerSetter() })
            .pipe(
                map((response: any) => response),
                catchError((error: HttpErrorResponse) => throwError(error))
            );
    }

    public isUserInRoles(requiredRoles: string[]): boolean {
        // requiredRoles undefined ise false döndür
        if (!requiredRoles || !Array.isArray(requiredRoles) || requiredRoles.length === 0) {
            return false;
        }

        const user = this.userAccessToken;

        if (!user || !Array.isArray(user.roles)) {
            return false;
        }

        const userRoles = new Set(this.normalizeRoles(user.roles).map(role => role.toLocaleLowerCase()));
        const hasRequiredRole = this.normalizeRoles(requiredRoles)
            .some(role => userRoles.has(role.toLocaleLowerCase()));
        return hasRequiredRole;
    }

    // search user role in globals.navizardSystemMenu
    public isAuthenticatedMenu(menuId: string | Guid): boolean {
        // if menuId is null or empty, return true
        if (!menuId) {
            return true;
        }

        const systemMenu = this.globals.navizardSystemMenuSource || this.globals.navizardSystemMenu;
        if (!systemMenu) {
            return false;
        }

        // also search in children and return found object
        const found = this.findMenu(systemMenu, menuId);
        if (!found) {
            return false;
        }

        if (!found.roles || found.roles.length === 0) {
            return true;
        }

        return this.isUserInRoles(found.roles);
    }

    findMenu(menuItems: any, menuId: string | Guid) {
        for (let i = 0; i < menuItems.length; i++) {
            if (String(menuItems[i].id).toLocaleLowerCase() === String(menuId).toLocaleLowerCase()) {
                return menuItems[i];
            }
            if (menuItems[i].children) {
                const found = this.findMenu(menuItems[i].children, menuId);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }

    private normalizeRoles(roles: unknown): string[] {
        const values = Array.isArray(roles) ? roles : [roles];
        const normalized: string[] = [];
        const seen = new Set<string>();

        values
            .flatMap((role) => String(role ?? '').split(','))
            .map((role) => role.trim())
            .filter(Boolean)
            .forEach((role) => {
                const key = role.toLocaleLowerCase();
                if (!seen.has(key)) {
                    seen.add(key);
                    normalized.push(role);
                }
            });

        return normalized;
    }

    private resolvePermissionService(): AppPermissionService | null {
        try {
            return this.injector.get(AppPermissionService, null);
        } catch {
            return null;
        }
    }
}

interface PublicAuthConfig {
    provider: string;
    projectUrl: string;
    anonKey: string;
    authBaseUrl: string;
}
